/**
 * Touch → intents: a positional drag-stick (US 12.01; the full visual d-pad
 * treatment is US 12.02, P1). Touch down anywhere sets an origin; dragging
 * beyond a dead zone produces a movement vector toward the drag. A short tap
 * without a drag fires INTERACT, so NPCs are usable on phones before the
 * dedicated button lands.
 */
import { worldBus } from '@shared/bus.js';
import { normaliseMove, type MoveVector } from '@shared/input.js';
import type { IntentHub, IntentSource } from './intent-hub.js';

const DEAD_ZONE_PX = 12; // below this, a touch is a tap, not a drag
const FULL_DEFLECTION_PX = 48; // drag distance for full speed
const TAP_MS = 250;

export function attachTouch(hub: IntentHub, element: HTMLElement): IntentSource {
  let pointerId: number | null = null;
  let originX = 0;
  let originY = 0;
  let startedAt = 0;
  let dragged = false;

  const source: IntentSource & { move: MoveVector } = {
    move: { x: 0, y: 0 },
    detach() {
      element.removeEventListener('pointerdown', onDown);
      element.removeEventListener('pointermove', onMove);
      element.removeEventListener('pointerup', onUp);
      element.removeEventListener('pointercancel', onUp);
    },
  };

  function onDown(ev: PointerEvent): void {
    if (ev.pointerType === 'mouse') return; // mouse users have the keyboard
    if (pointerId !== null) return; // first touch steers; ignore extras
    pointerId = ev.pointerId;
    originX = ev.clientX;
    originY = ev.clientY;
    startedAt = performance.now();
    dragged = false;
    worldBus.emit('input-device', { kind: 'touch' });
    worldBus.emit('touch-state', { active: true, originX, originY, dx: 0, dy: 0 });
    try {
      element.setPointerCapture(ev.pointerId);
    } catch {
      // Capture is an optimisation (keeps the drag when the finger leaves the
      // canvas); a pointer that cannot be captured still steers fine.
    }
  }

  function onMove(ev: PointerEvent): void {
    if (ev.pointerId !== pointerId) return;
    const dx = ev.clientX - originX;
    const dy = ev.clientY - originY;
    if (Math.hypot(dx, dy) < DEAD_ZONE_PX) {
      source.move = { x: 0, y: 0 };
      return;
    }
    dragged = true;
    source.move = normaliseMove(dx / FULL_DEFLECTION_PX, dy / FULL_DEFLECTION_PX);
    worldBus.emit('touch-state', { active: true, originX, originY, dx, dy });
  }

  function onUp(ev: PointerEvent): void {
    if (ev.pointerId !== pointerId) return;
    pointerId = null;
    source.move = { x: 0, y: 0 };
    worldBus.emit('touch-state', { active: false, originX: 0, originY: 0, dx: 0, dy: 0 });
    if (!dragged && performance.now() - startedAt < TAP_MS) {
      hub.press('INTERACT');
    }
  }

  element.addEventListener('pointerdown', onDown);
  element.addEventListener('pointermove', onMove);
  element.addEventListener('pointerup', onUp);
  element.addEventListener('pointercancel', onUp);
  hub.attach(source);
  return source;
}
